import * as React from "react";
import { CloudCheckIcon, LockIcon } from "../../../components/icons";
import { Alert } from "../../../components/ui/alert";
import { Separator, Spinner } from "../../../components/ui/feedback";
import { DocumentCheckOutAdapter, useHostAdapter } from "../../../platform";
import { useCheckOutVerification, verificationMessage } from "../hooks/useCheckOutVerification";

// The pane body for a host where the assistant does not reach yet: Excel today,
// PowerPoint next. There is no mode to switch into, so rather than an empty pane this
// answers the only question the add-in can currently answer -- is this document
// checked out to me, and what will the ribbon buttons do?
//
// Save and Check in stay on the ribbon rather than being duplicated here. They are
// one-press actions on the open document, and the overlay they raise already reports
// their result; a second pair of buttons in the pane would just be a second way to
// press the same thing.

const formatTimestamp = (value?: string | null): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleString();
};

export interface CheckOutStatusProps {
  /** Passed in rather than read from context: a host without one never renders this. */
  documents: DocumentCheckOutAdapter;
}

const CheckOutStatus = ({ documents }: CheckOutStatusProps) => {
  const host = useHostAdapter();
  const verification = useCheckOutVerification(documents);
  const saveUnavailable = documents.describeSaveUnavailable();

  if (verification.status === "loading") {
    return (
      <div className="flex justify-center p-8">
        <Spinner size={24} />
      </div>
    );
  }

  if (verification.status !== "ok") {
    return (
      <div className="flex flex-col gap-4 p-4">
        <Alert severity={verification.status === "no-identity" ? "info" : "warning"}>
          {verification.readFailure ?? verificationMessage(verification.status, verification.state)}
        </Alert>
        {verification.status === "no-identity" && (
          <p className="text-body2 text-muted-foreground">
            Check a document out from Jylo and it will open in {host.displayName} with Save and
            Check in available at the top of this pane.
          </p>
        )}
      </div>
    );
  }

  const lastSaved = formatTimestamp(verification.state?.lastSavedAt);
  const checkedOutAt = formatTimestamp(verification.state?.checkedOutAt);

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-row items-start gap-2">
        <LockIcon className="mt-0.5 text-primary" />
        <div className="min-w-0">
          <div className="truncate text-subtitle2">
            {verification.state?.documentName ?? "Checked out from Jylo"}
          </div>
          <div className="text-caption text-muted-foreground">
            Checked out to you
            {checkedOutAt ? ` since ${checkedOutAt}` : ""}
            {verification.state?.draftVersionNumber
              ? ` · draft of version ${verification.state.draftVersionNumber}`
              : ""}
          </div>
        </div>
      </div>

      <Separator />

      {saveUnavailable ? (
        <Alert severity="info">{saveUnavailable}</Alert>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex flex-row items-center gap-2">
            <CloudCheckIcon className="text-muted-foreground" />
            <p className="text-body2 text-muted-foreground">
              {lastSaved ? `Last saved to Jylo ${lastSaved}` : "Not yet saved to Jylo"}
            </p>
          </div>
          <p className="text-body2 text-muted-foreground">
            Use <strong>Save</strong> at the top of the pane to push your changes without creating a
            version, or <strong>Check in</strong> to publish a new version and end the check out.
          </p>
        </div>
      )}
    </div>
  );
};

export default CheckOutStatus;
